import { Heart, Sparkles } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '../lib/api';

interface Match {
  id: string;
  name: string;
  lastMessage: string;
  photo?: string;
  unread?: boolean;
}

interface ChatsViewProps {
  matches?: Match[];
  onBoostClick?: () => void;
  onChatClick?: (match: Match) => void;
}

export function ChatsView({ matches: propMatches, onBoostClick, onChatClick }: ChatsViewProps) {
  const [matches, setMatches] = useState<Match[]>(propMatches || []);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    // If matches are provided as props, use them (for backward compatibility)
    if (propMatches && propMatches.length > 0) {
      setMatches(propMatches);
      setLoading(false);
      return;
    }
    
    // Otherwise, fetch from API
    const fetchChats = async () => {
      try {
        setLoading(true);
        const response = await api.chat.getChats();
        const chatList: Match[] = response.data.map((chat) => ({
          id: String(chat.id),
          name: chat.name,
          lastMessage: chat.last_message || 'No messages yet',
          photo: chat.photo_url,
          unread: chat.unread_count > 0,
        }));
        setMatches(chatList);
      } catch (error) {
        console.error('Failed to fetch chats:', error);
        setMatches([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchChats();
  }, [propMatches]);
  
  const hasLikes = matches.length > 0;

  return (
    <div className="min-h-[calc(100vh-64px)] pb-20">
      {/* Boost Banner */}
      <div className="mx-4 mt-6 mb-6 p-4 rounded-2xl border backdrop-blur-xl flex items-center justify-between"
           style={{
             background: 'linear-gradient(135deg, rgba(0, 255, 136, 0.15) 0%, rgba(0, 100, 60, 0.1) 100%)',
             borderColor: 'rgba(0, 255, 136, 0.3)',
             boxShadow: '0 4px 20px rgba(0, 255, 136, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
           }}>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full border-2 flex items-center justify-center"
               style={{
                 background: 'rgba(0, 255, 136, 0.2)',
                 borderColor: 'rgba(0, 255, 136, 0.5)',
               }}>
            <Heart className="w-6 h-6" style={{ color: '#00FF88' }} />
          </div>
          <div>
            <p style={{ color: '#FFFFFF', marginBottom: '2px' }}>
              Boost Visibility
            </p>
            <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px' }}>
              x3 your matches this week
            </p>
          </div>
        </div>
        <button
          onClick={onBoostClick}
          className="px-5 py-2.5 rounded-full transition-all hover:scale-105"
          style={{
            background: '#00FF88',
            color: '#0F1419',
            boxShadow: '0 4px 12px rgba(0, 255, 136, 0.4)',
          }}
        >
          Get Boost
        </button>
      </div>

      {/* Chats Section */}
      <div className="px-4">
        <h2 className="mb-4" style={{ color: '#FFFFFF' }}>
          Chats
        </h2>

        {/* Chat List */}
        <div className="space-y-3">
          {loading ? (
            // Loading state
            <div className="flex items-center justify-center p-8">
              <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px' }}>
                Loading chats...
              </p>
            </div>
          ) : !hasLikes ? (
            // No Likes Yet
            <div className="flex items-center gap-4 p-4 rounded-2xl border backdrop-blur-xl"
                 style={{
                   background: 'rgba(255, 255, 255, 0.03)',
                   borderColor: 'rgba(255, 255, 255, 0.1)',
                 }}>
              <div className="w-14 h-14 rounded-full flex items-center justify-center"
                   style={{
                     background: 'rgba(0, 255, 136, 0.15)',
                     border: '2px solid rgba(0, 255, 136, 0.3)',
                   }}>
                <Heart className="w-7 h-7" style={{ color: '#00FF88' }} />
              </div>
              <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px' }}>
                You don&apos;t have any likes yet
              </p>
            </div>
          ) : (
            // Chat Items
            matches.map((match) => (
              <div
                key={match.id}
                onClick={() => onChatClick?.(match)}
                className="flex items-center gap-4 p-4 rounded-2xl border backdrop-blur-xl cursor-pointer transition-all hover:border-[#00FF88]/30"
                style={{
                  background: 'rgba(255, 255, 255, 0.03)',
                  borderColor: 'rgba(255, 255, 255, 0.1)',
                }}
              >
                {/* Avatar */}
                <div className="w-14 h-14 rounded-full flex items-center justify-center overflow-hidden bg-gradient-to-br from-[#00FF88] to-[#8B5CF6]"
                     style={{
                       boxShadow: '0 4px 12px rgba(0, 255, 136, 0.2)',
                     }}>
                  {match.photo ? (
                    <img src={match.photo} alt={match.name} className="w-full h-full object-cover" />
                  ) : (
                    <span style={{ color: '#FFFFFF', fontSize: '20px' }}>
                      {match.name.charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>

                {/* Chat Info */}
                <div className="flex-1">
                  <p style={{ color: '#FFFFFF', marginBottom: '4px' }}>
                    {match.name}
                  </p>
                  <p style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '13px' }}>
                    {match.lastMessage}
                  </p>
                </div>

                {/* Unread Indicator */}
                {match.unread && (
                  <div className="w-2.5 h-2.5 rounded-full"
                       style={{
                         background: '#00FF88',
                         boxShadow: '0 0 8px rgba(0, 255, 136, 0.6)',
                       }} />
                )}
              </div>
            ))
          )}
        </div>

        {/* Helper Text */}
        {!hasLikes && (
          <div className="mt-8 flex items-center gap-2 px-4 py-2 rounded-2xl backdrop-blur-xl border mx-auto"
               style={{
                 background: 'rgba(255, 255, 255, 0.05)',
                 borderColor: 'rgba(255, 255, 255, 0.1)',
                 boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.08)',
                 width: 'fit-content',
               }}>
            <Sparkles className="w-4 h-4" style={{ color: '#00FF88', filter: 'drop-shadow(0 0 4px rgba(0, 255, 136, 0.5))' }} />
            <p style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px' }}>
              Start swiping to get matches
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

